<template>
  <div>
    <ListTitle title="Device To Replace"></ListTitle>
    <IonList inset>
      <SerialNumberInput
        :disabled="!!deviceToReplace"
        id="replace-device-one"
        v-model:serial="deviceOne.value"
        :state="deviceOne.state"
        :errors="deviceOne.errors"
        :remote-reader-port-options="{ defaultToPort: 0 }"
        gray
      ></SerialNumberInput>
    </IonList>
    <Transition name="fade-down" mode="out-in">
      <div v-if="showDeviceTwo" class="mb-2">
        <DeviceLocationDetails
          class="px-6"
          v-if="deviceOneLocation?.isProgrammed && deviceOneLocation.programmedContexts[0]"
          :context="deviceOneLocation.programmedContexts[0]"
        ></DeviceLocationDetails>
        <ListTitle title="Device To Install"></ListTitle>
        <IonList inset>
          <SerialNumberInput
            id="replace-device-two"
            v-model:serial="deviceTwo.value"
            :state="deviceTwo.state"
            :errors="deviceTwo.errors"
            :remote-reader-port-options="{ defaultToPort: 0 }"
            gray
          ></SerialNumberInput>
          <div v-if="needsIMR">
            <StackedNumberInput
              v-model="imr.value"
              gray
              :title="needsIMRTwo ? 'Port One IMR' : 'Initial Meter Read'"
            ></StackedNumberInput>
          </div>
          <div v-if="needsIMRTwo">
            <StackedNumberInput v-model="imrTwo.value" gray title="Port Two IMR"></StackedNumberInput>
          </div>
          <PhotoInput
            gray
            v-model="photoBase64.value"
            :title="needsPhotoTwo ? 'New Port One Photo' : 'New Equipment Photo'"
          ></PhotoInput>
          <div v-if="needsPhotoTwo">
            <PhotoInput gray v-model="photoTwoBase64.value" title="New Port Two Photo"></PhotoInput>
          </div>
        </IonList>

        <Transition name="fade-down" mode="out-in">
          <PrimaryBlockButton :disabled="saving" @click="onSave()" v-if="canSave" class="mt-6 mx-4">
            <div class="flex items-center" v-if="!saving">Save</div>
            <div class="flex items-center" v-else><IonSpinner name="lines-small"></IonSpinner> Saving</div>
          </PrimaryBlockButton>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import SerialNumberInput from '@/components/forms/serial-input/SerialNumberInput.vue';
import ListTitle from '@/components/ListTitle.vue';
import { IonList, IonSpinner } from '@ionic/vue';
import DeviceLocationDetails from './DeviceLocationDetails.vue';
import StackedNumberInput from '@/components/forms/stacked/StackedNumberInput.vue';
import PhotoInput from '@/components/photos/PhotoInput.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import { AsyncFormControlValidator, useFormControl } from '@/composables/use-form-control';
import { RequiredFieldValidator, RequiredIfCondition } from '@/composables/validators/required';
import {
  AlreadyProgrammedDeviceValidator,
  DeviceFamilyValidator,
  RemoteReaderNonTRValidator,
  SpecificUtilityDeviceValidator,
} from '@/composables/validators/serial-form-control-validators';
import { DeviceFamily, HydrateRead, RFMeterRead, MeterConfig, UtilityTypes, IUtilityType } from '@ncss/models/dist/v3';
import { computed, onMounted, ref, watch } from 'vue';
import { DeviceLocation, ImageUpload } from 'common';
import { DeviceService, ReplaceDeviceResult } from '@/http-services/device.service';

const emit = defineEmits<{
  (e: 'onReplaceResult', result: ReplaceDeviceResult): void;
}>();
const props = defineProps<{
  deviceToReplace?: number;
}>();

const saving = ref(false);
const devices = new DeviceService();
const deviceOneLocation = ref<DeviceLocation>();
const deviceOneUtilityType = computed<IUtilityType | undefined>(() => {
  return deviceOneLocation.value?.programmedContexts?.[0]?.unit?.utilityType;
});
const deviceOne = useFormControl(() => undefined, {
  validators: [
    RequiredFieldValidator(),
    DeviceFamilyValidator([
      DeviceFamily.TRANSCEIVER,
      DeviceFamily.REMOTE_READER,
      DeviceFamily.INTEGRATED_METER,
      DeviceFamily.REPEATER,
      DeviceFamily.GATEWAY,
      DeviceFamily.TRANSMITTER_3RD_PARTY,
      DeviceFamily.REPEATER_3RD_PARTY,
      DeviceFamily.UNKNOWN_3RD_PARTY,
      DeviceFamily.LEAK_SENSOR,
    ]),
  ],
  asyncValidators: [AlreadyProgrammedDeviceValidator((l) => (deviceOneLocation.value = l))],
});
watch(
  () => props.deviceToReplace,
  (val) => {
    if (val) {
      deviceOne.value = val;
    }
  },
  { immediate: true }
);
const deviceTwo = useFormControl(() => undefined, {
  validators: [
    RequiredFieldValidator(),
    DeviceFamilyValidator([
      DeviceFamily.TRANSCEIVER,
      DeviceFamily.REMOTE_READER,
      DeviceFamily.INTEGRATED_METER,
      DeviceFamily.REPEATER,
      DeviceFamily.GATEWAY,
      DeviceFamily.TRANSMITTER_3RD_PARTY,
      DeviceFamily.REPEATER_3RD_PARTY,
      DeviceFamily.UNKNOWN_3RD_PARTY,
      DeviceFamily.LEAK_SENSOR,
    ]),
    RemoteReaderNonTRValidator(),
    SpecificUtilityDeviceValidator(() => deviceOneUtilityType.value?.family),
  ],
  asyncValidators: [createDeviceTwoValidator()],
});

const showDeviceTwo = computed(() => !!deviceOne.valid && !!deviceOne.value && !!deviceOneLocation.value);

const needsIMR = computed(() => {
  if (deviceOneLocation.value?.isProgrammed && deviceOneLocation.value.programmedContexts[0]?.latestRead) {
    const hydrated = HydrateRead(deviceOneLocation.value.programmedContexts[0].latestRead);
    if (hydrated instanceof RFMeterRead && hydrated.configType !== MeterConfig.ENCODER_IN) {
      return true;
    }
  }
  return false;
});
const needsIMRTwo = computed(() => {
  if (deviceOneLocation.value?.isProgrammed && deviceOneLocation.value.programmedContexts[0]?.unit?.portTwoLatestRead) {
    const hydrated = HydrateRead(deviceOneLocation.value.programmedContexts[0].unit.portTwoLatestRead);
    if (hydrated instanceof RFMeterRead && hydrated.configType !== MeterConfig.ENCODER_IN) {
      return true;
    }
    return false;
  }
});

const needsPhotoTwo = computed(() => {
  if (
    deviceOneLocation.value?.isProgrammed &&
    deviceOneLocation.value.programmedContexts[0]?.unit?.portTwoUtilityType
  ) {
    return true;
  }
  return false;
});

const imr = useFormControl(() => undefined, {
  validators: [RequiredIfCondition(() => !!needsIMR.value)],
});
const imrTwo = useFormControl(() => undefined, {
  validators: [RequiredIfCondition(() => !!needsIMRTwo.value)],
});
const photoBase64 = useFormControl(() => undefined, {
  validators: [RequiredFieldValidator()],
  validateIfTouched: true,
});
const photoTwoBase64 = useFormControl(() => undefined, {
  validators: [RequiredIfCondition(() => !!needsPhotoTwo.value)],
});

const canSave = computed<boolean>(() => {
  const imrValid = needsIMR.value ? imr.valid && imr.touched : true;
  const imrTwoValid = needsIMRTwo.value ? imrTwo.valid && imrTwo.touched : true;
  const photoValid = photoBase64.valid && photoBase64.touched;
  const photoTwoValid = needsPhotoTwo.value ? photoTwoBase64.valid && photoTwoBase64.touched : true;

  return (
    deviceOne.valid &&
    deviceOne.value &&
    deviceTwo.value &&
    deviceTwo.valid &&
    imrValid &&
    imrTwoValid &&
    photoValid &&
    photoTwoValid
  );
});

watch(showDeviceTwo, (show) => {
  if (!show) {
    deviceTwo.value = undefined;
    imr.value = undefined;
    imrTwo.value = undefined;
    photoBase64.value = undefined;
    photoTwoBase64.value = undefined;
  }
});

function createDeviceTwoValidator(): AsyncFormControlValidator {
  return async (val: number) => {
    const errors = await devices.validateReplaceDevice({
      deviceToReplace: deviceOne.value,
      deviceToInstall: val,
    });
    return { valid: errors.length ? false : true, errors };
  };
}

async function onSave() {
  saving.value = true;
  const context = deviceOneLocation.value?.programmedContexts?.[0];
  const photoUpload: ImageUpload | undefined =
    photoBase64.value && context
      ? {
          name: `replace-device-${deviceTwo.value.toString(16).toUpperCase()}`,
          base64: photoBase64.value,
          context: {
            propertyId: context.property.id,
            unitId: context.unit?.id,
            utilityTypeId: context.unit?.utilityType?.id,
            leakSensorLocation: context.unit?.leakSensorLocation,
          },
        }
      : undefined;
  const photoTwoUpload: ImageUpload | undefined =
    photoTwoBase64.value && context?.unit?.portTwoUtilityType
      ? {
          name: `replace-device-${deviceTwo.value.toString(16).toUpperCase()}`,
          base64: photoTwoBase64.value,
          context: {
            propertyId: context.property.id,
            unitId: context.unit?.id,
            utilityTypeId: context.unit?.portTwoUtilityType?.id,
          },
        }
      : undefined;
  const replaceResult = await devices.replaceDevice({
    deviceToReplace: deviceOne.value,
    deviceToInstall: deviceTwo.value,
    installInfo: { imr: imr.value, photo: photoUpload },
    portTwoInstallInfo: context?.unit?.portTwoUtilityType ? { imr: imrTwo.value, photo: photoTwoUpload } : undefined,
  });
  emit('onReplaceResult', replaceResult);
  if (replaceResult.success) {
    deviceOne.value = undefined;
  }
  saving.value = false;
}
</script>

<style scoped></style>
